import { clsx, type ClassValue } from 'clsx';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';
import {
  Document,
  KycDocument,
} from '../api/promoters/promoter-profile/promoter-profile.types';
import { WithdrawalStatus } from '../api/promoters/promoter-withdrawal/promoter-withdrawal.types';
import { Game } from '../api/promoters/promoters/promoters.types';
import type { FileType } from './constants';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const copyJSONToClipboard = async (json: Record<string, string>) => {
  try {
    const jsonString = JSON.stringify(json, null, 2); // pretty-print with indentation
    await navigator.clipboard.writeText(jsonString);
    toast.success(`Contest template with ID ${json?.id} Copied`, {
      className: 'bg-black text-white border-0',
      position: 'bottom-right',
      duration: 3000,
    });
  } catch (err) {
    console.error('Failed to copy!', err);
    toast.error("Sorry! Contest template couldn't be copied.", {
      className: 'bg-black text-white border-0',
      position: 'bottom-right',
      duration: 3000,
    });
  }
};

export function _formatToIndianNumber(num: number): string {
  if (isNaN(num)) return '';

  const [integer, decimal] = num.toString().split('.');
  const lastThree = integer.slice(-3);
  const otherDigits = integer.slice(0, -3);

  const formatted =
    otherDigits.replace(/\B(?=(\d{2})+(?!\d))/g, ',') +
    (otherDigits ? ',' : '') +
    lastThree;

  return decimal ? `${formatted}.${decimal}` : formatted;
}
function convertToReadableDate(isoDate: string) {
  const date = new Date(isoDate);

  const day = date.getDate();
  const month = date.toLocaleString('en-IN', { month: 'long' }); // "July"
  const year = date.getFullYear();
  const weekDay = date.toLocaleString('en-IN', { weekday: 'long' }); // "Saturday"

  return {
    dateString: `${day} ${month} ${year}`,
    weekDay,
  };
}

function convertToReadableTime(isoDate: string) {
  const date = new Date(isoDate);

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const period = hours24 >= 12 ? 'PM' : 'AM';

  return `${hours12}:${minutes} ${period}`;
}

/**
 * Converts an ISO date string to an exact readable time including seconds and milliseconds.
 * e.g. "2025-11-14T12:56:42.725Z" → "6:26:42.725 PM"
 */
function convertToExactReadableTime(isoDate: string): string {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';

  const hours24 = date.getHours();
  const hours12 = hours24 % 12 || 12;
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const ms = String(date.getMilliseconds()).padStart(3, '0');
  const period = hours24 >= 12 ? 'PM' : 'AM';

  return `${hours12}:${minutes}:${seconds}.${ms} ${period}`;
}

export function downloadBlob(
  blob: Blob,
  filename: string,
  onSuccess?: () => void
) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  onSuccess?.();
}

/** Parses `filename` from a typical `Content-Disposition` header; falls back when missing or malformed. */
export function filenameFromContentDisposition(
  contentDisposition: string | undefined,
  fallback: string
): string {
  return (
    contentDisposition?.match(/filename="?(.+)"?/)?.[1]?.trim() ?? fallback
  );
}

export function getFileTypeFromUrl(url: string): FileType {
  if (!url) return 'pdf';

  const withoutQuery = url.split('?')[0] || '';
  const extension = withoutQuery.split('.').pop()?.toLowerCase();

  if (
    extension === 'pdf' ||
    extension === 'jpg' ||
    extension === 'jpeg' ||
    extension === 'png' ||
    extension === 'webp'
  ) {
    return extension;
  }

  return 'pdf';
}

export {
  convertToExactReadableTime,
  convertToReadableDate,
  convertToReadableTime,
  copyJSONToClipboard,
};

export const getGameLabel = (value: string, games?: Game[]): string => {
  if (!games) return value;
  return games.find(game => game.value === value)?.label || value;
};

export const getGameValue = (label: string, games?: Game[]): string => {
  if (!games) return label;
  return games.find(game => game.label === label)?.value || label;
};

export const ALLOWED_STATUS_TRANSITIONS: Record<
  WithdrawalStatus,
  WithdrawalStatus[]
> = {
  PENDING: ['APPROVED', 'REJECTED'],
  APPROVED: ['PAID'],
  REJECTED: [],
  PAID: [],
};

export const toLocalDateString = (date: Date) => format(date, 'yyyy-MM-dd');

export const parseLocalDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

export const toIsoDate = (ms?: string) => {
  if (!ms || isNaN(Number(ms))) return undefined;

  const date = new Date(Number(ms));

  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');

  return `${y}-${m}-${d}`;
};

export const getIstDateParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);

  const lookup = Object.fromEntries(
    parts
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );

  return {
    year: lookup.year,
    month: lookup.month,
    day: lookup.day,
  };
};

export const getIstTimeParts = (date: Date) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  const lookup = Object.fromEntries(
    parts
      .filter(part => part.type !== 'literal')
      .map(part => [part.type, part.value])
  );

  return {
    hour: lookup.hour,
    minute: lookup.minute,
    second: lookup.second,
  };
};

export const buildIstDateTime = (selectedDate: Date) => {
  const now = new Date();
  const adjustedNow = new Date(now.getTime() + 5 * 60 * 1000);
  const { year, month, day } = getIstDateParts(selectedDate);
  const { hour, minute, second } = getIstTimeParts(adjustedNow);

  return `${year}-${month}-${day}T${hour}:${minute}:${second}+05:30`;
};

export const SYSTEM_KYC_SLOTS = [
  {
    key: 'adhar',
    label: 'Aadhaar Card',
    canonicalName: 'aadhaar',
  },
  {
    key: 'pan',
    label: 'PAN Card',
    canonicalName: 'pan',
  },
  {
    key: 'secondary',
    label: 'Secondary',
    canonicalName: 'secondary',
  },
] as const;

export function mapApiDocumentsToKycDocuments(
  apiDocs: Document[]
): KycDocument[] {
  return apiDocs.map((doc, index) => {
    const slot = SYSTEM_KYC_SLOTS[index];

    if (slot) {
      return {
        ...doc,
        docType: slot.key,
      };
    }
    return {
      ...doc,
      docType: 'secondary',
    };
  });
}
